import {
  Bookmark,
  Building2,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Plus,
  RefreshCw,
  Search,
  Upload,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { bulkClientAction, exportClientsCsv } from "@/features/clients/api";
import { BulkActionsBar } from "@/features/clients/components/bulk-actions-bar";
import { ClientRowActions } from "@/features/clients/components/client-row-actions";
import { ClientStatCards } from "@/features/clients/components/client-stat-cards";
import { FirmContextBanner } from "@/features/clients/components/firm-context-banner";
import { ImportClientsDialog } from "@/features/clients/components/import-clients-dialog";
import { TagMultiSelect } from "@/features/clients/components/tag-multi-select";
import { useClients } from "@/features/clients/hooks/use-clients";
import { useFirmContext } from "@/features/clients/hooks/use-firm-context";
import { useClientStats } from "@/features/clients/hooks/use-client-stats";
import type { BulkClientAction, ClientStatus, IrsStatus } from "@/features/clients/types";
import { ApiError } from "@/lib/api";
import { downloadTextFile } from "@/lib/download";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";
import { listTags, type Tag } from "@/features/settings/tags-api";
import { cn, formatCents } from "@/lib/utils";

const irsStatusBadgeClassName: Record<IrsStatus, string> = {
  "Notice Received": "border-transparent bg-info/15 text-info",
  "Pending POA": "border-transparent bg-gold/15 text-gold-foreground",
  "Installment Ag.": "border-transparent bg-success/15 text-success",
  Draft: "border-border bg-transparent text-muted-foreground",
  "OIC Submitted": "border-transparent bg-info/15 text-info",
};

const taskBadgeClassName: Record<"overdue" | "active" | "none", string> = {
  overdue: "border-transparent bg-destructive/15 text-destructive",
  active: "",
  none: "",
};

const COLUMN_COUNT = 10;

export default function ClientsPage() {
  const navigate = useNavigate();
  // A GA/SA or house CD may drill into a BP's book via ?firm=; otherwise `firm`
  // defaults to the house (CAP) book for GA/SA, or undefined (backend-scoped)
  // for everyone else.
  const { firm, actingFirmId, actingFirmName } = useFirmContext();
  const { clients, meta, loading, error, params, setParams, refetch } = useClients({ firm });
  const { stats, loading: statsLoading, refetch: refetchStats } = useClientStats(firm);
  const [searchInput, setSearchInput] = useState("");
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Debounce the search box so we don't fire a request per keystroke. Only push
  // an update when the trimmed value actually differs from the active param —
  // this avoids a redundant fetch on mount (empty === empty).
  useEffect(() => {
    const next = searchInput.trim() || undefined;
    if (next === (params.search ?? undefined)) return;
    const id = setTimeout(() => setParams({ search: next, page: 1 }), 300);
    return () => clearTimeout(id);
  }, [searchInput, params.search, setParams]);

  // Keep the query in step with the active firm when drilling between books
  // (the page stays mounted; only ?firm= changes).
  useEffect(() => {
    setParams({ firm, page: 1 });
  }, [firm, setParams]);

  // Load the firm's practitioners for the filter dropdown — scoped to the active
  // firm so a BP workspace shows the BP's practitioners, not the house's.
  useEffect(() => {
    let active = true;
    listPractitioners(firm)
      .then((list) => active && setPractitioners(list))
      .catch(() => active && setPractitioners([]));
    return () => {
      active = false;
    };
  }, [firm]);

  // Load the firm's tags once, for the tag filter (archived kept — you can
  // still filter by an archived tag even though it's no longer assignable).
  useEffect(() => {
    let active = true;
    listTags()
      .then((list) => active && setTags(list))
      .catch(() => active && setTags([]));
    return () => {
      active = false;
    };
  }, []);

  // A changed query (page, filters, search) renders different rows, so any
  // prior selection is stale.
  useEffect(() => {
    setSelected(new Set());
  }, [params]);

  function openClient(clientId: string) {
    navigate(`/client/${clientId}/overview`);
  }

  function refreshAll() {
    refetch();
    refetchStats();
  }

  function toggleRow(clientId: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(clientId);
      else next.delete(clientId);
      return next;
    });
  }

  const allSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));
  const someSelected = clients.some((c) => selected.has(c.id));

  function toggleAll(checked: boolean) {
    // Current page only — a cross-page "select all N" needs server-side
    // select-by-filter semantics the bulk endpoint doesn't have.
    setSelected(checked ? new Set(clients.map((c) => c.id)) : new Set());
  }

  async function runBulk(action: BulkClientAction, status?: ClientStatus) {
    setBulkBusy(true);
    try {
      const result = await bulkClientAction([...selected], action, status);
      if (result.updated < result.requested) {
        toast.warning(
          `${result.updated} of ${result.requested} clients updated — some were no longer available. List refreshed.`,
        );
      } else {
        toast.success(
          action === "delete"
            ? `Deleted ${result.updated} client${result.updated === 1 ? "" : "s"}.`
            : `Updated ${result.updated} client${result.updated === 1 ? "" : "s"}.`,
        );
      }
      setSelected(new Set());
      refreshAll();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      // Use what's in the search box, not the debounced param — an export
      // clicked mid-debounce must not silently use the previous filter.
      const csv = await exportClientsCsv({ ...params, search: searchInput.trim() || undefined });
      const date = new Date().toISOString().slice(0, 10);
      downloadTextFile(`clients-${date}.csv`, csv, "text/csv");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  }

  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const rangeStart = meta.count === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + clients.length - 1;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-foreground font-heading text-3xl font-semibold tracking-[-0.02em]">
            Clients
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage all individual and business clients across your practice.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" aria-label="Refresh" onClick={refreshAll}>
            <RefreshCw />
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload /> Import
          </Button>
          <Button variant="outline" disabled={exporting} onClick={() => void handleExport()}>
            <Download /> {exporting ? "Exporting…" : "Export"}
          </Button>
          <Button onClick={() => navigate("/clients/new")}>
            <Plus /> Add Client
          </Button>
        </div>
      </div>

      {/* Acting-on-behalf-of-a-firm banner (drill-in from the BP directory) */}
      {actingFirmId && <FirmContextBanner firmName={actingFirmName} />}

      {/* Summary cards */}
      <ClientStatCards stats={stats} loading={statsLoading} />

      {/* Table area */}
      <Card className="gap-0 overflow-hidden py-0">
        {/* Filters bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-4">
          <div className="flex min-w-[300px] flex-1 items-center gap-3">
            <div className="relative w-72">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                type="text"
                placeholder="Search name, ID, or case #..."
                className="pl-9"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Select
              value={params.type ?? "all-types"}
              onValueChange={(v) =>
                setParams({
                  type: v === "all-types" ? undefined : (v as "individual" | "business"),
                  page: 1,
                })
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-types">All Types</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={params.status ?? "all-statuses"}
              onValueChange={(v) =>
                setParams({
                  status: v === "all-statuses" ? undefined : (v as ClientStatus),
                  page: 1,
                })
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-statuses">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="onboarding">Onboarding</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={params.practitioner ?? "any-practitioner"}
              onValueChange={(v) =>
                setParams({ practitioner: v === "any-practitioner" ? undefined : v, page: 1 })
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any-practitioner">Any Practitioner</SelectItem>
                {practitioners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tags.length > 0 && (
              <TagMultiSelect
                tags={tags}
                selected={params.tag ?? []}
                onChange={(ids) => setParams({ tag: ids.length ? ids : undefined, page: 1 })}
              />
            )}
            <Button variant="outline" size="sm">
              <Filter /> Filters
            </Button>
          </div>
          <Button variant="secondary" size="sm">
            <Bookmark /> Save View
          </Button>
        </div>

        {/* Bulk actions (visible while rows are selected) */}
        <BulkActionsBar
          selectedCount={selected.size}
          busy={bulkBusy}
          onSetStatus={(status) => void runBulk("set_status", status)}
          onDelete={() => void runBulk("delete")}
          onClear={() => setSelected(new Set())}
        />

        {/* Table */}
        <div className="max-h-[calc(100vh-380px)] overflow-y-auto">
          <Table>
            <TableHeader className="bg-card sticky top-0 z-10">
              <TableRow>
                <TableHead className="w-12 text-center">
                  <Checkbox
                    aria-label="Select all clients on this page"
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(checked) => toggleAll(checked === true)}
                  />
                </TableHead>
                <TableHead>Client Name / ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Case #</TableHead>
                <TableHead>IRS Status</TableHead>
                <TableHead>Practitioner</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Tasks</TableHead>
                <TableHead>Last Activity</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground py-12 text-center">
                    Loading clients…
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="py-12 text-center">
                    <p className="text-destructive text-sm">{error}</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={refetch}>
                      <RefreshCw /> Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground py-12 text-center">
                    No clients found.
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow
                    key={client.id}
                    tabIndex={0}
                    onClick={() => openClient(client.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target === e.currentTarget) openClient(client.id);
                    }}
                    className="focus-visible:bg-muted/50 cursor-pointer focus-visible:outline-none"
                  >
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        aria-label={`Select ${client.name}`}
                        checked={selected.has(client.id)}
                        onCheckedChange={(checked) => toggleRow(client.id, checked === true)}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/client/${client.id}/overview`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-foreground font-semibold outline-none hover:underline focus-visible:underline"
                      >
                        {client.name}
                      </Link>
                      <div className="text-muted-foreground mt-0.5 font-mono text-xs">
                        {client.idLabel}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground inline-flex items-center gap-1">
                        {client.type === "Business" ? (
                          <Building2 className="size-3.5" />
                        ) : (
                          <User className="size-3.5" />
                        )}
                        {client.type}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono">
                      {client.caseNumber}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={irsStatusBadgeClassName[client.irsStatus]}>
                        {client.irsStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-foreground">{client.practitioner}</TableCell>
                    <TableCell className="text-foreground font-medium">
                      {formatCents(client.balanceCents)}
                    </TableCell>
                    <TableCell>
                      {client.tasks.variant === "overdue" ? (
                        <Badge variant="outline" className={taskBadgeClassName.overdue}>
                          {client.tasks.label}
                        </Badge>
                      ) : (
                        <span
                          className={cn(
                            "text-xs",
                            client.tasks.variant === "active"
                              ? "text-muted-foreground"
                              : "text-muted-foreground/60",
                          )}
                        >
                          {client.tasks.label}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {client.lastActivity}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <ClientRowActions client={client} onChanged={refreshAll} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between border-t px-4 py-4">
          <p className="text-muted-foreground text-sm">
            Showing{" "}
            <span className="text-foreground font-medium">
              {rangeStart}–{rangeEnd}
            </span>{" "}
            of <span className="text-foreground font-medium">{meta.count}</span> clients
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">Rows per page:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => setParams({ pageSize: Number(v), page: 1 })}
              >
                <SelectTrigger size="sm" className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={!meta.previous}
                onClick={() => setParams({ page: page - 1 })}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={!meta.next}
                onClick={() => setParams({ page: page + 1 })}
                aria-label="Next page"
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Growing-your-practice hint (mockup) */}
      <section className="border-border/70 flex flex-wrap items-center gap-5 rounded-xl border border-dashed bg-card/50 px-6 py-5">
        <span className="bg-accent text-info flex size-11 shrink-0 items-center justify-center rounded-xl">
          <Upload className="size-[18px]" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[15px] font-semibold">Growing your practice?</span>
          <span className="text-muted-foreground text-[13px]">
            Import clients in bulk from a CSV, or add them one at a time — each one starts with a
            guided onboarding checklist.
          </span>
        </div>
        <Button variant="outline" onClick={() => setImportOpen(true)}>
          <Upload /> Import from CSV
        </Button>
      </section>

      <ImportClientsDialog open={importOpen} onOpenChange={setImportOpen} onImported={refreshAll} />
    </div>
  );
}
